/**
 * Voice Lock Test Overlay — full-screen display for testing voice match.
 *
 * Uses the user's configured threshold from localStorage:
 * - Green (>= threshold) — likely same speaker (match)
 * - Yellow (>= threshold - 0.15) — uncertain
 * - Red (< threshold - 0.15) — likely different speaker (no match)
 *
 * Also shows an audio level indicator to confirm mic is working.
 */

import styles from './VoiceLockTest.module.css';

interface VoiceLockTestProps {
  /** Latest test score (0.0–1.0), null if no score yet */
  testScore: number | null;
  /** Current audio level (0.0–1.0) */
  audioLevel: number;
  /** User-configured similarity threshold */
  threshold: number;
  /** Close the test overlay */
  onClose: () => void;
}

function getScoreClass(score: number | null, threshold: number): string {
  if (score === null) return styles.scoreNone;
  if (score >= threshold) return styles.scoreGreen;
  if (score >= threshold - 0.15) return styles.scoreYellow;
  return styles.scoreRed;
}

function getScoreLabel(score: number | null, threshold: number): string {
  if (score === null) return 'Waiting for speech…';
  if (score >= threshold) return '✓ Match';
  if (score >= threshold - 0.15) return '⚠ Uncertain';
  return '✗ No match';
}

export function VoiceLockTest({ testScore, audioLevel, threshold, onClose }: VoiceLockTestProps) {
  const displayScore = testScore !== null ? testScore.toFixed(2) : '—';

  return (
    <div className={styles.overlay}>
      <div className={styles.title}>🔒 Voice Lock Test</div>
      <div className={styles.hint}>
        Speak naturally. The score shows how closely your voice matches the enrolled profile.
      </div>

      {/* Audio level indicator */}
      <div className={styles.levelRow}>
        <div
          className={`${styles.levelDot} ${audioLevel > 0.05 ? styles.levelDotActive : ''}`}
          style={{
            transform: `scale(${1 + audioLevel * 0.6})`,
            opacity: 0.4 + audioLevel * 0.6,
          }}
        />
        <div className={styles.levelBar}>
          <div
            className={styles.levelFill}
            style={{ width: `${Math.min(100, audioLevel * 100)}%` }}
          />
        </div>
      </div>

      <div className={styles.label}>Similarity</div>
      <div className={`${styles.scoreDisplay} ${getScoreClass(testScore, threshold)}`}>
        {displayScore}
      </div>
      <div className={styles.thresholdHint}>
        Threshold: {threshold.toFixed(2)}
      </div>
      <div className={styles.hint}>
        {getScoreLabel(testScore, threshold)}
      </div>
      <button className={styles.closeBtn} onClick={onClose} type="button">
        Close
      </button>
    </div>
  );
}
